import { inject, injectable } from '@/inversify';
import Stateable from '@/modules/common/interfaces/stateable.interface';
import { KEY } from '@/inversify.keys';
import RankingCommonService, { RankingCommonServiceS } from '@/modules/common/modules/ranking/ranking-common.service';
import type DocumentFiltersService from '@/modules/document-filters/document-filters.service';
import type HelperService from '@/modules/common/services/helper.service';
import type ClusterService from '@/modules/cluster/cluster.service';

import ClusterStore from './store/cluster.store';
import ClusterApiService, { ClusterApiServiceS } from './cluster-api.service';
import ClusterRankingModel from './models/cluster-ranking.model';
import PAGES from '../common/constants/pages.constant';
import ProvidersStore from '../providers/store/providers.store';
import NotificationService from '../notifications/notification.service';

import type StoreFacade from '../common/services/store-facade';
import type ProvidersService from '../providers/providers.service';
import type { CompsetsService } from '../compsets/types';

export const ClusterRankingServiceS = Symbol.for('ClusterRankingServiceS');
@injectable()
export default class ClusterRankingService implements Stateable {
    @inject(KEY.StoreFacade) storeFacade!: StoreFacade;
    @inject(KEY.HelperService) private helperService!: HelperService;
    @inject(KEY.DocumentFiltersService) private documentFiltersService!: DocumentFiltersService;
    @inject(KEY.ClusterService) private clusterService!: ClusterService;
    @inject(KEY.NotificationService) private notificationService!: NotificationService;
    @inject(RankingCommonServiceS) private rankingCommonService!: RankingCommonService;
    @inject(ClusterApiServiceS) private clusterApiService!: ClusterApiService;
    @inject(KEY.ProvidersService) private providersService!: ProvidersService;
    @inject(KEY.CompsetsService) private compsetsService!: CompsetsService;

    readonly storeState: ClusterStore = this.storeFacade.getState('ClusterStore');
    readonly providerStoreState: ProvidersStore = this.storeFacade.getState('ProvidersStore');

    constructor() {
        this.storeFacade.watch(() => [
            this.documentFiltersService.storeState.settings.month,
            this.documentFiltersService.storeState.settings.year,
        ], this.clusterService.resetLoading.bind(this));
    }

    get clusterRankingsData() {
        this.helperService.dynamicLoading(
            this.storeState.loading,
            this.clusterService.loadData.bind(this.clusterService, PAGES.RANKING),
        );
        return this.storeState.clusterHotels as ClusterRankingModel[];
    }

    get hotelsCount() {
        this.helperService.dynamicLoading(
            this.storeState.loading,
            this.clusterService.loadData.bind(this.clusterService, PAGES.RANKING),
        );
        return this.storeState.totalCount as number;
    }

    private competititveHotels(document: any, provider: string, currentHotelId: number | null) {
        if (!document || !currentHotelId) {
            return null;
        }

        const hotels = this.rankingCommonService.hotels(document, provider);

        if (!hotels) {
            return null;
        }

        const { [currentHotelId]: _, ...competitors } = hotels;

        return competitors;
    }

    minMaxCompetitiveRating(document: any, provider: string, hotelId: number): [number, number] | null {
        const competitors = this.competititveHotels(document, provider, hotelId);

        if (!competitors || !Object.keys(competitors).length) {
            return null;
        }

        return this.rankingCommonService.minMaxRating(competitors);
    }

    minMaxCompetitiveReview(document: any, provider: string, hotelId: number): [number, number] | null {
        const competitors = this.competititveHotels(document, provider, hotelId);

        if (!competitors || !Object.keys(competitors).length) {
            return null;
        }

        return this.rankingCommonService.minMaxReview(competitors);
    }

    get providers() {
        return this.providersService.userProviders.marketProviders;
    }

    hotels(document: any, provider: string) {
        if (!document) {
            return null;
        }

        return this.rankingCommonService.hotels(document, provider);
    }

    async downloadExcel() {
        const excelData = await this.clusterApiService.getGuestReviewsExcelDocument();

        if (excelData) {
            this.notificationService.excel({
                title: 'titles.excel-report',
                message: excelData.fileName,
            }, excelData);
        }
    }
}
